<template>
  <b-modal
    class="mc-modal"
    id="modal-last-data"
    title="Last Check Data"
    size="xl"
    centered
    scrollable
    @hidden="cleanupWebSocket"
  >
    <b-row class="equal-height-row">
      <!-- Kolom Peta -->
      <b-col cols="12" md="6" class="mb-3 map-col">
        <MapData
          :lat="vehiclePosition.latitude"
          :long="vehiclePosition.longitude"
          :direction="vehiclePosition.direction"
          :markerIcon="vehicleIconSrc"
        />
      </b-col>

      <!-- Kolom Data dengan scroll -->
      <b-col cols="12" md="6" class="mb-3 data-col">
        <div class="data-scroll-container">
          <div class="d-flex align-items-center mb-3">
            <i class="mc-fa icon-fill_bar-chart me-2"></i>
            <h5 class="mb-0 text-primary"><strong>Device Check Data</strong></h5>
          </div>

          <!-- Device Information -->
          <b-card class="mb-3 mc-modal__border" border-variant="info">
            <h6 class="card-title d-flex align-items-center mb-3">
              <i class="mc-fa icon-fill_smartphone me-2"></i>
              <span>Device Information</span>
            </h6>
            <b-row>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>IMEI</span>
                  <strong>{{ vehicle_last_data.imei || '-' }}</strong>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Last Packet</span>
                  <span>{{
                    moment(new Date(vehiclePosition?.last_packet)).format('DD MMM YYYY HH:mm:ss') ||
                    '-'
                  }}</span>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span
                    >Last Receive
                    <i
                      v-b-tooltip.hover.top="$t('vehicle.status.lastCheckDataTooltip')"
                      class="mc-fa icon-fill_info"
                    ></i>
                  </span>
                  <span>{{
                    moment(new Date(vehiclePosition?.last_receive)).format(
                      'DD MMM YYYY HH:mm:ss'
                    ) || '-'
                  }}</span>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span
                    >Last Position Status
                    <i
                      v-b-tooltip.hover.top="$t('vehicle.status.lastPositionTooltip')"
                      class="mc-fa icon-fill_info"
                    ></i>
                  </span>
                  <span>{{
                    moment(new Date(vehiclePosition?.last_position_status)).format(
                      'DD MMM YYYY HH:mm:ss'
                    ) || '-'
                  }}</span>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Battery</span>
                  <strong>{{ vehiclePosition?.battery }} %</strong>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1 align-items-center">
                  <span>Speed</span>
                  <b-badge class="bg-primary text-white">{{
                    (vehiclePosition?.speed || 0) + ' km/h'
                  }}</b-badge>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>ACC</span>
                  <span :class="vehiclePosition?.engine_on ? 'text-success' : 'text-danger'">
                    {{ vehiclePosition?.engine_on ? 'Hidup' : 'Mati' }}
                  </span>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Offline Timeout</span>
                  <span>{{ vehicle_last_data?.offline_timeout ?? '-' }}</span>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Live View Status</span>
                  <div :class="getLiveViewStatus(vehiclePosition?.motion_status).bg">
                    <span class="text-white">{{
                      getLiveViewStatus(vehiclePosition?.motion_status).text
                    }}</span>
                  </div>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="d-flex justify-content-between mb-1">
                  <span>Plat Nomor</span>
                  <span>{{ vehicle_last_data?.license_plate ?? '-' }}</span>
                </div>
                <div class="mc-modal__line-bottom"></div>
              </b-col>
            </b-row>
          </b-card>

          <!-- Location Data -->
          <div class="card mb-3 mc-modal__border">
            <div class="card-body">
              <h6 class="card-title d-flex align-items-center mb-3">
                <i class="mc-fa icon-fill_pin me-2"></i>
                <span>Location Data</span>
              </h6>
              <b-row>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Latitude</span>
                    <strong>{{
                      vehiclePosition?.latitude != null ? vehiclePosition.latitude : '-'
                    }}</strong>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Longitude</span>
                    <strong>{{
                      vehiclePosition?.longitude != null ? vehiclePosition.longitude : '-'
                    }}</strong>
                  </div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Satellites</span>
                    <span>{{ vehiclePosition?.gps_satellites ?? '-' }}</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Signal (Value)</span>
                    <span>{{ vehiclePosition?.signal_strength ?? '-' }}</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1 align-items-center">
                    <span>Signal Strength</span>
                    <b-badge :class="getSignalType(vehiclePosition).bg">
                      <i
                        :class="`mc-fa icon-fill_signal-${
                          getSignalType(vehiclePosition).icon
                        } me-2`"
                      ></i>
                      <span :class="getSignalType(vehiclePosition).color">{{
                        getSignalType(vehiclePosition).text
                      }}</span>
                    </b-badge>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="12" class="mb-3">
                  <strong class="text-bold">Address</strong>
                  <br />
                  <p>{{ vehiclePosition?.address }}</p>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
              </b-row>
            </div>
          </div>

          <!-- Sensor Readings (existing) -->
          <div class="card mc-modal__border mb-3">
            <div class="card-body">
              <h6 class="card-title d-flex align-items-center mb-3">
                <i class="mc-fa icon-fill_settings me-2"></i>
                <span>Sensor Readings</span>
              </h6>
              <b-row>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Fuel Filtered</span>
                    <strong>
                      {{
                        vehicle_last_data?.fuel_filtered != null &&
                        vehicle_last_data?.fuel_filtered !== '-'
                          ? Number(vehicle_last_data.fuel_filtered).toFixed(2) + ' liter'
                          : '-'
                      }}
                    </strong>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Fuel Raw</span>
                    <strong>
                      {{
                        vehiclePosition?.fuel?.[0] != null && vehiclePosition?.fuel?.[0] !== '-'
                          ? Number(vehiclePosition.fuel[0]).toFixed(2) + '%'
                          : '-'
                      }}
                    </strong>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Door 1</span>
                    <span>{{ getDoorStatus(vehiclePosition?.door1_status) }}</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Door 2</span>
                    <span>{{ getDoorStatus(vehiclePosition?.door2_status) }}</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Rotation Count</span>
                    <span>{{
                      vehiclePosition?.rotation_count != null ? vehiclePosition.rotation_count : '-'
                    }}</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Rotation Direction</span>
                    <span>{{
                      vehiclePosition?.rotation_direction != null
                        ? vehiclePosition.rotation_direction
                        : '-'
                    }}</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Temperature 1</span>
                    <span>{{ vehiclePosition?.temperature?.[0] || '-' }} °C</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Temperature 2</span>
                    <span>{{ vehiclePosition?.temperature?.[1] || '-' }} °C</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Temperature 3</span>
                    <span>{{ vehiclePosition?.temperature?.[3] || '-' }} °C</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span>Voltage</span>
                    <span>{{ vehiclePosition?.accu_voltage || '-' }} V</span>
                  </div>
                  <div class="mc-modal__line-bottom"></div>
                </b-col>
              </b-row>
            </div>
          </div>

          <!-- ==================== OBD DATA ==================== -->
          <div class="card mc-modal__border" v-if="hasObdData || canbusFormulas.length > 0">
            <div class="card-body">
              <h6 class="card-title d-flex align-items-center mb-3">
                <i class="mc-fa icon-outline_odometer me-2"></i>
                <span>OBD Data <span class="badge bg-warning text-dark ms-2">(testing)</span></span>
              </h6>

              <!-- Jika belum ada data formula, tampilkan loading atau pesan -->
              <div v-if="isLoadingFormulas" class="text-center py-3">
                <b-spinner variant="primary" label="Loading..."></b-spinner>
              </div>

              <!-- Tampilkan field-field dari formula -->
              <template v-else>
                <!-- Kita bisa bagi menjadi beberapa kolom, misal 2 kolom -->
                <b-row v-for="(row, idx) in chunkedObdFields" :key="idx" class="obd-row">
                  <b-col cols="12" md="6" v-for="field in row" :key="field.key" class="mb-3">
                    <div class="field-item">
                      <div class="d-flex justify-content-between align-items-start">
                        <span class="obd-label">
                          {{ field.label }}
                          <!-- Tooltip bisa ditambahkan jika ada deskripsi, tapi API tidak menyediakan -->
                          <i
                            v-b-tooltip.hover.top="'Nilai dari ' + field.key"
                            class="mc-fa icon-fill_info ms-1"
                          ></i>
                        </span>
                        <strong class="obd-value">{{ field.value }}</strong>
                      </div>
                      <div class="mc-modal__line-bottom"></div>
                    </div>
                  </b-col>
                </b-row>

                <!-- Jika tidak ada field yang cocok -->
                <div
                  v-if="obdDisplayFields.length === 0 && !isLoadingFormulas"
                  class="text-muted text-center py-3"
                >
                  Tidak ada data OBD yang tersedia untuk kendaraan ini.
                </div>
              </template>
            </div>
          </div>
          <!-- ==================== END OBD DATA ==================== -->
        </div>
      </b-col>
    </b-row>

    <template #modal-footer="{}">
      <b-row class="mc-modal__filter-footer mx-0">
        <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
          <b-button variant="outline-primary" @click="$emit('close')" class="me-2">
            Close
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import MapData from './MapLastCheckData'
import { icon } from 'leaflet'
import { convertKeysToSnakeCase } from '../../../../../helpers/validators'
import moment from 'moment'
import { GetAddressResult } from '@/services/api/map'
import * as encryptJwtApi from '@/services/rest-api-tools/lib/encrypt-jwt/encrypt-jwt'
import * as canbusFormulasApi from '@/services/rest-api-sms/lib/canbus-formulas/canbus-formulas'

export default {
  name: 'ModalLastCheckData',
  components: {
    MapData,
  },
  props: {
    vehicle_last_data: {
      type: Object,
      required: true,
    },
    region: {
      type: String,
      default: 'SG',
    },
    device_code: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      vehiclePosition: {
        latitude: 0,
        longitude: 0,
        direction: 0,
        temperature: [],
        fuel: 0,
        address: '-',
      },
      ws: null,
      vehicleObdData: {},
      canbusFormulas: [],
      isLoadingFormulas: false,
    }
  },
  computed: {
    // ========== Tampilkan SEMUA field dari canbus formula ==========
    obdDisplayFields() {
      const obd = this.obdData || {}
      const formulas = this.canbusFormulas || []

      // Pastikan formulas adalah array, jika tidak gunakan []
      const formulaList = Array.isArray(formulas) ? formulas : []

      // Loop semua formula tanpa filter
      return formulaList
        .filter((f) => f && f.stored_result_can) // pastikan ada stored_result_can
        .map((f) => {
          const key = f.stored_result_can
          const rawValue = obd[key] !== undefined ? obd[key] : null
          // Gunakan label dari API jika ada, atau buat sendiri
          const label = f.label || this.formatObdKey(key)
          const unit = f.unit || ''
          const displayValue = this.formatObdValue(key, rawValue, unit)
          return {
            key,
            label,
            value: displayValue,
            raw: rawValue,
            unit,
          }
        })
    },

    // ========== Field yang tidak ada di formula (untuk Additional) ==========
    remainingObdFields() {
      const obd = this.obdData || {}
      const formulas = this.canbusFormulas || []
      const formulaKeys = formulas.map((f) => f.stored_result_can).filter(Boolean)
      const remaining = {}
      for (const key in obd) {
        if (key !== 'vehicle_id' && key !== 'sent_on' && !formulaKeys.includes(key)) {
          remaining[key] = obd[key]
        }
      }
      return remaining
    },

    vehicleIconSrc() {
      const status = this.vehiclePosition?.motion_status ?? this.vehicle_last_data?.motion_status

      const iconVehicle =
        this.vehiclePosition?.vehicle?.vehicleType?.icon ??
        this.vehicle_last_data?.vehicle?.vehicleType?.icon ??
        'van'

      const color =
        {
          P: 'red',
          A: 'blue',
          M: 'green',
          I: 'orange',
          O: 'grey',
          U: 'grey',
        }[status] || 'grey'

      const path = require(`@/assets/images/vehicles/${iconVehicle}/${iconVehicle}-${color}.svg`)

      return icon({
        iconUrl: path,
        iconSize: [52, 57],
        iconAnchor: [16, 37],
      })
    },

    obdData() {
      if (this.vehicleObdData && this.vehicleObdData.vehicle_id) {
        return this.vehicleObdData
      }
      return this.vehicle_last_data?.obd_status || this.vehicle_last_data?.obd || {}
    },

    hasObdData() {
      return Object.keys(this.obdData).length > 0 && this.obdData.vehicle_id
    },

    chunkedObdFields() {
      const fields = this.obdDisplayFields
      const chunked = []
      for (let i = 0; i < fields.length; i += 2) {
        chunked.push(fields.slice(i, i + 2))
      }
      return chunked
    },
  },

  watch: {
    vehicle_last_data: {
      immediate: true,
      async handler(newVal) {
        if (newVal?.vehicle_id) {
          this.vehicleObdData = {}
          this.setVehiclePosition(newVal?.latitude, newVal?.longitude, 0, newVal)
          this.startCombined()
          await this.fetchCanbusFormulas(newVal.vehicle_id)
        }
      },
    },
  },

  beforeDestroy() {
    this.cleanupWebSocket()
  },

  methods: {
    // ========== Format nilai OBD dengan mapping lengkap ==========
    formatObdValue(key, rawValue, unit) {
      // Jika rawValue null atau undefined, tampilkan '-'
      if (rawValue === null || rawValue === undefined) return '-'

      // Mapping khusus untuk field dengan enum
      const mappings = {
        charge_connection_status: {
          0: 'Not Charging',
          1: 'Charging',
          2: 'Complete',
        },
        charging_status: {
          '00': 'Not Charging',
          '01': 'Complete',
          '02': 'Parking',
          '03': 'Invalid',
          '04': 'Regen',
          '06': 'Abnormal',
        },
        vehicle_state: {
          '00': 'Not Charging',
          '01': 'Complete',
          '02': 'Parking',
          '03': 'Invalid',
          '04': 'Regen',
          '06': 'Abnormal',
        },
        pto_status: {
          0: 'Tidak Aktif',
          1: 'Aktif',
          false: 'Tidak Aktif',
          true: 'Aktif',
        },
      }

      // Daftar field yang menggunakan getStatusLabel (boolean / ON/OFF/OPEN/CLOSE)
      const statusFields = [
        'pedal_brake_status',
        'pedal_clutch_status',
        'handbrake_status',
        'brake_switch',
        'driver_seatbelt_attached',
        'anti_theft_warning_status',
        'immobilizer_indication_request',
      ]

      // Daftar field pintu yang menggunakan getDoorStatusObd
      const doorFields = [
        'door_status_front_left',
        'door_status_front_right',
        'door_status_rear_left',
        'door_status_rear_right',
        'door_status_trunk',
        'door_status_hood',
      ]

      // 1. Cek mapping khusus
      if (mappings[key] && mappings[key][rawValue] !== undefined) {
        return mappings[key][rawValue]
      }

      // 2. Cek field status
      if (statusFields.includes(key)) {
        return this.getStatusLabel(rawValue)
      }

      // 3. Cek field pintu
      if (doorFields.includes(key)) {
        return this.getDoorStatusObd(rawValue)
      }

      // 4. Untuk field numerik biasa
      let displayValue = rawValue
      if (typeof rawValue === 'number' && !Number.isInteger(rawValue)) {
        displayValue = rawValue.toFixed(2)
      }
      if (unit && displayValue !== '-') {
        if (this.device_code === 2010 && unit === 'km') unit = 'm'
        displayValue += ` ${unit}`
      }
      return displayValue
    },

    async fetchCanbusFormulas(vehicleId) {
      this.isLoadingFormulas = true
      try {
        const response = await canbusFormulasApi.getByVehicleId(vehicleId, this.region)
        // Pastikan response.data adalah array, jika tidak gunakan []
        this.canbusFormulas = Array.isArray(response?.data) ? response.data : []
        console.log('Canbus formulas:', this.canbusFormulas) // untuk debugging
      } catch (error) {
        console.error('Failed to fetch canbus formulas:', error)
        this.canbusFormulas = []
      } finally {
        this.isLoadingFormulas = false
      }
    },

    getStatusLabel(value) {
      if (value === true || value === 1 || value === '1') return 'Aktif'
      if (value === false || value === 0 || value === '0') return 'Tidak Aktif'
      if (value === 'ON' || value === 'on') return 'ON'
      if (value === 'OFF' || value === 'off') return 'OFF'
      if (value === 'OPEN' || value === 'open') return 'OPEN'
      if (value === 'CLOSE' || value === 'close') return 'CLOSE'
      return '-'
    },

    getDoorStatusObd(value) {
      if (value === true || value === 1 || value === '1' || value === 'OPEN' || value === 'open')
        return 'OPEN'
      if (value === false || value === 0 || value === '0' || value === 'CLOSE' || value === 'close')
        return 'CLOSE'
      return '-'
    },

    getChargingStatus(value) {
      if (value === '1' || value === 1 || value === '01' || value === 'on' || value === 'ON')
        return 'Charging'
      if (value === '0' || value === '00' || value === 0 || value === 'off' || value === 'OFF')
        return 'Not Charging'
      return '-'
    },

    getDoorStatus(status) {
      if (status === 1) return 'CLOSED'
      if (status === 0) return 'OPEN'
      return '-'
    },

    formatObdKey(key) {
      // Ubah snake_case menjadi judul dengan spasi
      return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    },

    moment,

    // ========== WebSocket ==========
    cleanupWebSocket() {
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
      this.vehicleObdData = {}
    },

    async startCombined() {
      const token = await this.getCombinedToken()
      if (token) {
        this.startWsCombined(token)
      }
    },

    async getCombinedToken() {
      const vehicleId = this.vehicle_last_data.vehicle_id
      if (!vehicleId) return null
      try {
        const payload = {
          payload: {
            tags: [`gps-${vehicleId}`, `obd-${vehicleId}`],
          },
        }
        const response = await encryptJwtApi.store(payload)
        return response.data
      } catch (error) {
        console.error('Gagal mendapatkan token gabungan:', error)
        return null
      }
    },

    startWsCombined(token) {
      this.cleanupWebSocket()
      let wsUrl = process.env.VUE_APP_WS_URL
      if (this.region === 'ID') {
        wsUrl = process.env.VUE_APP_WS_URL_ID
      }
      this.ws = new WebSocket(`${wsUrl}/ws?token=${localStorage.getItem('wsToken')}`)

      this.ws.onopen = () => {
        this.ws.send(
          JSON.stringify({
            type: 'update_tags',
            data: token,
          })
        )
      }

      this.ws.onmessage = (evt) => {
        const message = JSON.parse(evt.data)
        if (message?.type?.startsWith('gps-')) {
          const vehicleData = message.payload
          this.setVehiclePosition(
            vehicleData.latitude,
            vehicleData.longitude,
            vehicleData.direction,
            vehicleData
          )
        } else if (message?.type?.startsWith('obd-')) {
          const vehicleData = message.payload
          this.setVehiclePositionObd(vehicleData)
        }
      }

      this.ws.onclose = () => {
        console.log('WebSocket closed')
      }
    },

    // ========== Update data ==========
    async setVehiclePosition(latitude, longitude, direction, restData) {
      let address = '-'
      try {
        address = (await GetAddressResult(latitude, longitude)) ?? '-'
      } catch (err) {
        console.log(err)
      } finally {
        const convertedKey = convertKeysToSnakeCase(restData)
        const fuel_filtered =
          convertedKey?.fuel_filtered !== null &&
          convertedKey?.fuel_filtered !== 0 &&
          convertedKey?.fuel_capacity !== null &&
          convertedKey?.fuel_capacity !== 0
            ? (convertedKey?.fuel_filtered * convertedKey?.fuel_capacity) / 100
            : '-'

        this.vehiclePosition = {
          latitude,
          longitude,
          direction,
          ...convertedKey,
          fuel_filtered,
          battery: convertedKey?.battery_percentage ?? convertedKey.battery,
          address,
        }
      }
    },

    async setVehiclePositionObd(data) {
      const convertedKey = convertKeysToSnakeCase(data)
      if (
        convertedKey.odometer != null &&
        !isNaN(convertedKey.odometer) &&
        this.device_code === 2010
      ) {
        convertedKey.odometer = (convertedKey.odometer * 1000).toFixed(2)
      }
      this.vehicleObdData = { ...convertedKey }
    },

    // ========== Helper ==========
    getSignalType(device) {
      let data = {
        bg: 'bg-light',
        text: 'Disconnect',
        icon: 'disconnect',
        color: 'text-dark',
      }
      const lastReceive = new Date(device.last_receive)
      const now = new Date()
      const diffMinutes = (now.getTime() - lastReceive.getTime()) / 1000 / 60
      if (isNaN(lastReceive.getTime()) || diffMinutes > 30) {
        return data
      }
      const signal = device.signal_strength
      if (signal > 2)
        data = {
          bg: 'badge-soft-success',
          text: 'High',
          icon: 'high',
          color: 'text-success',
        }
      if (signal === 2)
        data = {
          bg: 'badge-soft-warning',
          text: 'Medium',
          icon: 'med',
          color: 'text-warning',
        }
      if (signal === 1)
        data = {
          bg: 'badge-soft-danger',
          text: 'Low',
          icon: 'low',
          color: 'text-danger',
        }
      return data
    },

    getLiveViewStatus(status) {
      let data = { bg: 'bg-secondary', text: 'Offline' }
      switch (status) {
        case 'M':
          data = { bg: 'bg-success', text: 'Moving' }
          break
        case 'I':
          data = { bg: 'bg-warning', text: 'Idle' }
          break
        case 'P':
          data = { bg: 'bg-danger', text: 'Parking' }
          break
        case 'A':
          data = { bg: 'bg-primary', text: 'Assisted' }
          break
        default:
          data = { bg: 'bg-secondary', text: 'Offline' }
      }
      return data
    },
  },
}
</script>

<style scoped>
/* Membuat kedua kolom memiliki tinggi yang sama */
.equal-height-row {
  display: flex;
  flex-wrap: wrap;
  height: 70vh;
  /* tinggi sesuai kebutuhan, bisa disesuaikan */
  min-height: 400px;
  /* opsional, agar tidak terlalu kecil */
}

.map-col,
.data-col {
  display: flex;
  flex-direction: column;
  height: 100%;
  /* mengisi tinggi row */
}

.map-col {
  height: 100%;
}

/* Kontainer data di kanan: scroll jika konten melebihi tinggi */
.data-scroll-container {
  height: 100%;
  overflow-y: auto;
  padding-right: 8px;
}

/* Atur tinggi map agar mengisi penuh kolom */
::v-deep .map-col .leaflet-container {
  height: 100% !important;
  min-height: 0;
}

.map-col .leaflet-container {
  height: 100% !important;
  min-height: 400px;
  /* hapus jika sudah pakai 100% */
}

/* ===== OBD Data ===== */
.obd-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
  /* jarak antar baris */
}

.obd-row .col-md-6,
.obd-row .col-md-12 {
  display: flex;
  flex-direction: column;
}

.field-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.field-item .mc-modal__line-bottom {
  margin-top: auto;
}

/* Optional: beri sedikit ruang ekstra di atas subjudul */
.obd-row .text-muted.mb-2 {
  margin-top: 0.5rem;
}

/* Perbaikan layout OBD agar tidak overlap */
.field-item .d-flex {
  display: flex;
  flex-wrap: wrap; /* biar turun ke baris baru jika perlu */
  align-items: baseline;
  gap: 4px 8px; /* jarak antar item */
}

.obd-label {
  flex: 0 1 auto; /* tidak memaksa shrink */
  word-break: break-word;
  max-width: 60%; /* batasi agar tidak terlalu panjang */
}

.obd-value {
  flex: 0 1 auto;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
  white-space: normal; /* pastikan bisa wrap */
}

/* Hilangkan flex yang memaksa value tetap di kanan */
.obd-row .col-md-6,
.obd-row .col-md-12 {
  margin-bottom: 0.75rem;
}
</style>
